import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Badge,
  Spinner,
  Table,
  Form,
  InputGroup,
  Alert,
  Dropdown,
  Modal,
} from 'react-bootstrap';
import { 
  FaArrowLeft, 
  FaFile, 
  FaFilePdf, 
  FaFileImage, 
  FaFileAlt, 
  FaDownload, 
  FaEye, 
  FaTrash, 
  FaSync, 
  FaSearch, 
  FaFilter,
  FaSort,
  FaSortUp,
  FaSortDown,
  FaCalendar,
  FaUser,
  FaEnvelope,
  FaExternalLinkAlt,
  FaFileContract,
  FaFileInvoice,
  FaFileMedical,
  FaFileSignature,
  FaPaperclip
} from 'react-icons/fa';
import axios from 'axios';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const API_BASE = 'http://34.206.193.218:4001/api/admin';

const Forms = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [sortField, setSortField] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');
  const [selectedForm, setSelectedForm] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  
  const token = localStorage.getItem('adminToken') || localStorage.getItem('token');

  const fetchForms = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const res = await axios.get(
        `${API_BASE}/data/getFormContractsByProject/${projectId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success) {
        setForms(res.data.forms || []);
      } else {
        setError('Failed to load forms');
        toast.error('Failed to load forms');
      }
    } catch (error) {
      console.error('Fetch forms error:', error);
      setError('Error loading forms');
      toast.error('Error loading forms');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId) {
      fetchForms();
    }
  }, [projectId]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getFileIcon = (fileName) => {
    const ext = fileName?.split('.').pop().toLowerCase();
    if (ext === 'pdf') return <FaFilePdf className="text-danger" />;
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(ext)) return <FaFileImage className="text-success" />;
    if (['doc', 'docx'].includes(ext)) return <FaFileAlt className="text-primary" />;
    return <FaFile className="text-secondary" />;
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'Contract': return <FaFileContract className="text-primary" />;
      case 'Invoice': return <FaFileInvoice className="text-success" />;
      case 'Medical': return <FaFileMedical className="text-danger" />;
      case 'Miscellaneous': return <FaFileSignature className="text-warning" />;
      default: return <FaFileAlt className="text-secondary" />;
    }
  };

  const getTypeBadge = (type, otherTypeName) => {
    const displayType = type === 'Miscellaneous' && otherTypeName ? otherTypeName : type;
    
    let variant = 'secondary';
    switch (type) {
      case 'Contract': variant = 'primary'; break;
      case 'Invoice': variant = 'success'; break;
      case 'Medical': variant = 'danger'; break;
      case 'Miscellaneous': variant = 'warning'; break;
      default: variant = 'secondary';
    }
    
    return <Badge bg={variant}>{displayType}</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    
    const months = Math.floor(diffInSeconds / 2592000);
    if (months < 1) return `${Math.floor(diffInSeconds / 86400)} days ago`;
    if (months < 12) return `${months} month${months !== 1 ? 's' : ''} ago`;
    
    const years = Math.floor(months / 12);
    return `${years} year${years !== 1 ? 's' : ''} ago`;
  };

  const handlePreview = (form) => {
    setSelectedForm(form);
    setShowPreviewModal(true);
  };

  const handleDownload = async (form) => {
    try {
      const response = await fetch(form.fileUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = form.fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Download started');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  const filteredForms = forms
    .filter(form => {
      // Search filter
      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase();
        return (
          form.name?.toLowerCase().includes(searchLower) ||
          form.fileName?.toLowerCase().includes(searchLower) ||
          form.type?.toLowerCase().includes(searchLower) ||
          form.otherTypeName?.toLowerCase().includes(searchLower) ||
          form.createdBy?.name?.toLowerCase().includes(searchLower) ||
          form.createdBy?.email?.toLowerCase().includes(searchLower)
        );
      }
      return true;
    })
    .filter(form => {
      // Type filter
      if (filterType === 'all') return true;
      return form.type === filterType;
    })
    .sort((a, b) => {
      let aVal, bVal;
      
      switch (sortField) {
        case 'name':
          aVal = a.name?.toLowerCase() || '';
          bVal = b.name?.toLowerCase() || '';
          break;
        case 'type':
          aVal = a.type?.toLowerCase() || '';
          bVal = b.type?.toLowerCase() || '';
          break;
        case 'createdBy':
          aVal = a.createdBy?.name?.toLowerCase() || '';
          bVal = b.createdBy?.name?.toLowerCase() || '';
          break;
        case 'createdAt':
          aVal = new Date(a.createdAt).getTime();
          bVal = new Date(b.createdAt).getTime();
          break;
        default:
          aVal = new Date(a.createdAt).getTime();
          bVal = new Date(b.createdAt).getTime();
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

  const getUniqueTypes = () => {
    const types = forms.map(form => form.type);
    return ['all', ...new Set(types)];
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '100vh' }}>
        <Spinner animation="border" variant="primary" />
        <span className="ms-3">Loading forms and contracts...</span>
      </div>
    );
  }

  if (error) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          <h4>Error Loading Forms</h4>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={fetchForms}>
            <FaSync className="me-2" />
            Retry
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* HEADER */}
      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-start mb-4">
            <div>
              <Button
                variant="outline-primary"
                onClick={() => navigate(-1)}
                className="mb-3 d-flex align-items-center"
              >
                <FaArrowLeft className="me-2" />
                Back to Projects List
              </Button>
              <h2 className="fw-bold mb-2">
                <FaFileContract className="me-3" />
                Forms & Contracts
              </h2>
              <div className="d-flex align-items-center flex-wrap gap-2">
                <Badge bg="primary" className="px-3 py-2">
                  Project ID: {projectId?.substring(0, 8)}...
                </Badge>
                <Badge bg="info" className="px-3 py-2">
                  <FaFile className="me-2" />
                  {forms.length} Document{forms.length !== 1 ? 's' : ''}
                </Badge>
              </div>
            </div>
            
            <div className="d-flex gap-2">
              <Button
                variant="outline-primary"
                onClick={fetchForms}
                className="d-flex align-items-center"
              >
                <FaSync className="me-2" />
                Refresh
              </Button>
            </div>
          </div>

          {/* FILTERS & SEARCH */}
          <Card className="mb-4 border-0 shadow-sm">
            <Card.Body>
              <Row className="g-3">
                <Col md={6}>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaSearch />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Search by name, file, type, or author..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    {searchTerm && (
                      <Button
                        variant="outline-secondary"
                        onClick={() => setSearchTerm('')}
                      >
                        Clear
                      </Button>
                    )}
                  </InputGroup>
                </Col>
                
                <Col md={3}>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaFilter />
                    </InputGroup.Text>
                    <Form.Select
                      value={filterType}
                      onChange={(e) => setFilterType(e.target.value)}
                    >
                      <option value="all">All Types</option>
                      {getUniqueTypes()
                        .filter(type => type !== 'all')
                        .map(type => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))
                      }
                    </Form.Select>
                  </InputGroup>
                </Col>
                
                <Col md={3}>
                  <InputGroup>
                    <InputGroup.Text>
                      <FaSort />
                    </InputGroup.Text>
                    <Form.Select
                      value={sortField}
                      onChange={(e) => setSortField(e.target.value)}
                    >
                      <option value="createdAt">Sort by Date</option>
                      <option value="name">Sort by Name</option>
                      <option value="type">Sort by Type</option>
                      <option value="createdBy">Sort by Author</option>
                    </Form.Select>
                    <Button
                      variant={sortDirection === 'desc' ? 'primary' : 'secondary'}
                      onClick={() => setSortDirection(sortDirection === 'desc' ? 'asc' : 'desc')}
                    >
                      {sortDirection === 'desc' ? <FaSortDown /> : <FaSortUp />}
                    </Button>
                  </InputGroup>
                </Col>
              </Row>
            </Card.Body>
          </Card>

          {/* FORMS LIST */}
          {filteredForms.length === 0 ? (
            <Card className="border-0 shadow-sm">
              <Card.Body className="text-center py-5">
                <FaFile size={64} className="text-muted mb-3" />
                <h4 className="mb-3">No Forms Found</h4>
                <p className="text-muted mb-4">
                  {searchTerm || filterType !== 'all'
                    ? 'No forms match your search criteria'
                    : 'No forms have been uploaded for this project yet'}
                </p>
                {(searchTerm || filterType !== 'all') && (
                  <Button 
                    variant="outline-primary" 
                    onClick={() => {
                      setSearchTerm('');
                      setFilterType('all');
                    }}
                  >
                    Clear Filters
                  </Button>
                )}
              </Card.Body>
            </Card>
          ) : (
            <>
              <Card className="border-0 shadow-sm mb-4">
                <Card.Body className="p-0">
                  <Table hover responsive className="mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th 
                          className="cursor-pointer"
                          onClick={() => handleSort('name')}
                        >
                          <div className="d-flex align-items-center">
                            Document
                            {sortField === 'name' && (
                              sortDirection === 'asc' ? <FaSortUp className="ms-1" /> : <FaSortDown className="ms-1" />
                            )}
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer"
                          onClick={() => handleSort('type')}
                        >
                          <div className="d-flex align-items-center">
                            Type
                            {sortField === 'type' && (
                              sortDirection === 'asc' ? <FaSortUp className="ms-1" /> : <FaSortDown className="ms-1" />
                            )}
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer"
                          onClick={() => handleSort('createdBy')}
                        >
                          <div className="d-flex align-items-center">
                            Uploaded By
                            {sortField === 'createdBy' && (
                              sortDirection === 'asc' ? <FaSortUp className="ms-1" /> : <FaSortDown className="ms-1" />
                            )}
                          </div>
                        </th>
                        <th 
                          className="cursor-pointer"
                          onClick={() => handleSort('createdAt')}
                        >
                          <div className="d-flex align-items-center">
                            Upload Date
                            {sortField === 'createdAt' && (
                              sortDirection === 'asc' ? <FaSortUp className="ms-1" /> : <FaSortDown className="ms-1" />
                            )}
                          </div>
                        </th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredForms.map((form) => (
                        <tr key={form._id}>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="me-3">
                                {getFileIcon(form.fileName)}
                              </div>
                              <div>
                                <div className="fw-medium">{form.name}</div>
                                <div className="small text-muted">
                                  <FaPaperclip size={12} className="me-1" />
                                  {form.fileName}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              <div className="me-2">
                                {getTypeIcon(form.type)}
                              </div>
                              {getTypeBadge(form.type, form.otherTypeName)}
                            </div>
                          </td>
                          <td>
                            <div>
                              <div className="fw-medium">{form.createdBy?.name}</div>
                              <div className="small text-muted">
                                <FaEnvelope size={12} className="me-1" />
                                {form.createdBy?.email}
                              </div>
                            </div>
                          </td>
                          <td>
                            <div>
                              <div className="fw-medium">{formatDate(form.createdAt)}</div>
                              <div className="small text-muted">
                                <FaCalendar size={12} className="me-1" />
                                {getTimeAgo(form.createdAt)}
                              </div>
                            </div>
                          </td>
                          <td>
                            <div className="d-flex gap-2">
                              <Button
                                variant="outline-primary"
                                size="sm"
                                onClick={() => handlePreview(form)}
                                title="Preview"
                              >
                                <FaEye />
                              </Button>
                              {/* <Button
                                variant="outline-success"
                                size="sm"
                                onClick={() => handleDownload(form)}
                                title="Download"
                              >
                                <FaDownload />
                              </Button> */}
                              <Button
                                variant="outline-info"
                                size="sm"
                                onClick={() => window.open(form.fileUrl, '_blank')}
                                title="Open in New Tab"
                              >
                                <FaExternalLinkAlt />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>

              {/* SUMMARY STATS */}
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  <Row>
                    <Col md={3}>
                      <Card className="border-0 bg-primary bg-opacity-10 text-center">
                        <Card.Body>
                          <div className="display-6 text-primary mb-2">
                            {forms.length}
                          </div>
                          <div className="small text-muted">Total Documents</div>
                        </Card.Body>
                      </Card>
                    </Col>
                    
                    <Col md={3}>
                      <Card className="border-0 bg-success bg-opacity-10 text-center">
                        <Card.Body>
                          <div className="display-6 text-success mb-2">
                            {forms.filter(f => f.type === 'Contract').length}
                          </div>
                          <div className="small text-muted">Contracts</div>
                        </Card.Body>
                      </Card>
                    </Col>
                    
                    <Col md={3}>
                      <Card className="border-0 bg-warning bg-opacity-10 text-center">
                        <Card.Body>
                          <div className="display-6 text-warning mb-2">
                            {forms.filter(f => f.type === 'Miscellaneous').length}
                          </div>
                          <div className="small text-muted">Miscellaneous</div>
                        </Card.Body>
                      </Card>
                    </Col>
                    
                    <Col md={3}>
                      <Card className="border-0 bg-info bg-opacity-10 text-center">
                        <Card.Body>
                          <div className="display-6 text-info mb-2">
                            {getUniqueTypes().length - 1}
                          </div>
                          <div className="small text-muted">Document Types</div>
                        </Card.Body>
                      </Card>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </>
          )}

          {/* NO DATA INFO */}
          {forms.length === 0 && !loading && !error && (
            <Alert variant="info" className="mt-4">
              <div className="d-flex align-items-center">
                <FaFile className="me-3" size={24} />
                <div>
                  <h6 className="mb-1">No Documents Available</h6>
                  <p className="mb-0">This project doesn't have any forms or contracts uploaded yet.</p>
                </div>
              </div>
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* PREVIEW MODAL */}
      <Modal
        show={showPreviewModal}
        onHide={() => setShowPreviewModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <FaEye className="me-2" />
            Document Preview
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedForm && (
            <>
              <Row className="mb-4">
                <Col md={6}>
                  <div className="mb-3">
                    <div className="small text-muted">Document Name</div>
                    <div className="fw-medium">{selectedForm.name}</div>
                  </div>
                  <div className="mb-3">
                    <div className="small text-muted">File Name</div>
                    <div className="fw-medium">{selectedForm.fileName}</div>
                  </div>
                  <div className="mb-3">
                    <div className="small text-muted">Document Type</div>
                    <div>{getTypeBadge(selectedForm.type, selectedForm.otherTypeName)}</div>
                  </div>
                </Col>
                <Col md={6}>
                  <div className="mb-3">
                    <div className="small text-muted">Uploaded By</div>
                    <div className="fw-medium">{selectedForm.createdBy?.name}</div>
                    <div className="small text-muted">
                      <FaEnvelope size={12} className="me-1" />
                      {selectedForm.createdBy?.email}
                    </div>
                  </div>
                  <div className="mb-3">
                    <div className="small text-muted">Upload Date</div>
                    <div className="fw-medium">{formatDate(selectedForm.createdAt)}</div>
                    <div className="small text-muted">
                      <FaCalendar size={12} className="me-1" />
                      {getTimeAgo(selectedForm.createdAt)}
                    </div>
                  </div>
                </Col>
              </Row>
              
              <div className="text-center">
                {selectedForm.fileUrl.toLowerCase().endsWith('.pdf') ? (
                  <iframe
                    src={selectedForm.fileUrl}
                    title={selectedForm.name}
                    style={{ width: '100%', height: '500px', border: '1px solid #dee2e6' }}
                  />
                ) : (
                  <img
                    src={selectedForm.fileUrl}
                    alt={selectedForm.name}
                    style={{ maxWidth: '100%', maxHeight: '500px', border: '1px solid #dee2e6' }}
                  />
                )}
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreviewModal(false)}>
            Close
          </Button>
          <Button variant="primary" onClick={() => selectedForm && handleDownload(selectedForm)}>
            <FaDownload className="me-2" />
            Download
          </Button>
          <Button variant="info" onClick={() => window.open(selectedForm?.fileUrl, '_blank')}>
            <FaExternalLinkAlt className="me-2" />
            Open in New Tab
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default Forms;